// forgot-password-modal.component.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { authBackendEndPointToken } from '../app.config';

// Update this to match your environment path
// import { environment } from '../../environments/environment';

@Component({
  selector: 'app-forgot-password-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <div class="forgot-password-modal">
      <div class="modal-header">
        <h2 mat-dialog-title>Reset Password</h2>
        <button mat-icon-button (click)="onClose()" class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <div class="modal-description" *ngIf="!submitted">
          Enter your email address and we'll send you a link to reset your password.
        </div>

        <div class="success-message" *ngIf="submitted">
          <mat-icon class="success-icon">check_circle</mat-icon>
          <p>If your email is registered, you will receive a password reset link shortly.</p>
          <p class="sub-text">Please check your inbox and spam folder.</p>
        </div>

        <form [formGroup]="forgotPasswordForm" (ngSubmit)="onSubmit()" *ngIf="!submitted">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Email Address</mat-label>
            <mat-icon matPrefix>mail</mat-icon>
            <input
              matInput
              type="email"
              formControlName="email"
              placeholder="Enter your email"
              required />
            <mat-error *ngIf="forgotPasswordForm.get('email')?.hasError('required')">
              Email is required
            </mat-error>
            <mat-error *ngIf="forgotPasswordForm.get('email')?.hasError('email')">
              Please enter a valid email
            </mat-error>
          </mat-form-field>

          <div class="error-message" *ngIf="errorMessage">
            <mat-icon>error</mat-icon>
            <span>{{ errorMessage }}</span>
          </div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button
          mat-button
          (click)="onClose()"
          type="button"
          class="cancel-button">
          {{ submitted ? 'Close' : 'Cancel' }}
        </button>
        <button
          mat-flat-button
          color="primary"
          class="primary-button"
          (click)="onSubmit()"
          [disabled]="loading || submitted"
          *ngIf="!submitted">
          {{ loading ? 'Sending...' : 'Send Reset Link' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .forgot-password-modal {
      min-width: 400px;
      max-width: 500px;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px 0;

      h2 {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .close-button {
        margin-right: -12px;
      }
    }

    mat-dialog-content {
      padding: 20px 24px;
      max-height: 400px;
      overflow-y: auto;
    }

    .modal-description {
      color: #666;
      font-size: 14px;
      margin-bottom: 24px;
      line-height: 1.5;
    }

    .success-message {
      text-align: center;
      padding: 20px 0;

      .success-icon {
        font-size: 64px;
        width: 64px;
        height: 64px;
        color: #4caf50;
        margin-bottom: 16px;
      }

      p {
        margin: 8px 0;
        color: #333;
        font-size: 15px;
        line-height: 1.5;
      }

      .sub-text {
        color: #666;
        font-size: 13px;
      }
    }

    .full-width {
      width: 100%;
      margin-bottom: 8px;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #d32f2f;
      font-size: 13px;
      margin-top: 8px;
      padding: 8px 12px;
      background-color: #ffebee;
      border-radius: 4px;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    mat-dialog-actions {
      padding: 12px 24px 20px;
      margin: 0;

      .cancel-button {
        margin-right: 8px;
      }

      .primary-button {
        min-width: 140px;
      }
    }

    ::ng-deep .mat-mdc-dialog-container {
      border-radius: 12px !important;
    }
  `]
})
export class ForgotPasswordModalComponent {
  forgotPasswordForm: FormGroup;
  loading = false;
  submitted = false;
  errorMessage = '';

  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private dialogRef = inject(MatDialogRef<ForgotPasswordModalComponent>);
  authBackendApiBaseUrl = inject(authBackendEndPointToken)
  constructor() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  onSubmit(): void {
    if (this.forgotPasswordForm.invalid || this.loading || this.submitted) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const email = this.forgotPasswordForm.value.email?.trim();

    

    this.http.post(`${this.authBackendApiBaseUrl}/api/auth/forgot-password`, { email })
      .subscribe({
        next: (response: any) => {
          console.log('✅ Password reset email sent');
          this.loading = false;
          this.submitted = true;
        },
        error: (error: any) => {
          console.error('❌ Forgot password error:', error);
          this.loading = false;
          this.errorMessage = error.error?.message || 'Failed to send reset link. Please try again.';
        }
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
